import { CommentOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import {
  WebDashboardEligibility,
  FeatureFlag,
  SubscriptionBillingInterval,
  SubscriptionPlanType,
  SubscriptionScheduledChangeType,
} from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { Progress } from "antd";
import clsx from "clsx";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import {
  Button,
  ChatSmileIcon,
  Checkbox,
  ConfirmBox,
  Excel,
  FlexColumn,
  FlexRow,
  GiftIcon,
  message,
  NewWindow3,
  Switch,
  SwitchLabel,
  Tag,
  Tick,
  Tooltip,
  Typography,
} from "../../../components";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import { BContext, PricingSummary } from "../../../types";
import {
  getFreeTrialNumFractionalDaysLeft,
  getPlanCardSeatPrice,
  getPricingSummary,
  planNameFromType,
  pluralize,
  useTrack,
} from "../../../utils";
import { BillingContext } from "../Billing";
import {
  AddWebDashboardModal,
  CancelPlanHelpText,
  FAQSection,
  PriceSummaryProrated,
  WEB_DASHBOARD_BILLED_PER_BUILDER,
  WEB_DASHBOARD_MAX_PER_BUILDER,
} from "../components";
import { ContactSalesModal } from "../components/ContactSalesModal";
import { shouldBlockEnterpriseCheckout } from "../enterpriseBlocker.utils";
import {
  AI_DASHBOARDS_BILLING_PAGE_GATE_PROPS,
  AiDashboardsGateProps,
  isAiDashboardsUpgradeSource,
} from "../planChooserSource.utils";
import {
  getTooManyDashboardsMessage,
  SELF_SERVE_WEB_DASHBOARD_LIMIT,
  shouldShowWebDashboardOffer,
} from "../webDashboardEligibility.utils";

const FIRST_COLUMN_WIDTH = 270;
// Self-serve design: labels column + 12px card gutter puts the Starter card 243px from the title.
const SELF_SERVE_LABELS_WIDTH = 231;
const SELF_SERVE_SIDE_COLUMN_WIDTH = 327;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  width: calc(100vw - 670px);
  min-width: 805px;
  gap: 10px;
`;

const Banner = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  padding: 4px 8px;
  background-color: ${COLORS.black4};
  border-radius: 6px;
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BannerAction = styled.a`
  color: ${COLORS.black85};
  font-weight: 600;
  margin-right: 4px;

  &:hover {
    color: ${COLORS.black85};
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;

  h2 {
    font-weight: bold;
    color: ${COLORS.black85};
    font-size: 24px;
    width: ${FIRST_COLUMN_WIDTH}px;
    margin: 0;
  }

  .desc {
    color: ${COLORS.black45};
  }
`;

const Bold = styled.span`
  font-weight: bold;
`;

const SelectorWrap = styled.div`
  display: flex;
  color: ${COLORS.black85};
  align-items: flex-end;

  .column {
    &.labels {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 700px;
      width: ${FIRST_COLUMN_WIDTH}px;
    }

    &.plan {
      text-align: center;
      width: 230px;

      .planHeader {
        text-align: center;
        margin-left: 10px;
        padding: 8px 16px;
        font-weight: bold;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
      }

      .planBox {
        margin-left: 10px;
        padding: 20px;
        border-radius: 16px;

        &.noTopCurve {
          border-top-left-radius: 0px;
          border-top-right-radius: 0px;
        }
      }

      .planHeader,
      .planBox {
        background: ${COLORS.black4};
        color: ${COLORS.black85};
      }

      &.current {
        .planHeader,
        .planBox {
          background: ${COLORS.coeblue11};
        }
        .planHeader {
          margin-top: -38px;
          color: ${COLORS.coeblue4};
          background: ${COLORS.coeblue12};
        }
      }

      /* "Most Popular" ribbon on the Pro card (self-serve AI Dashboards design). Mirrors the
         .current .planHeader treatment so the card box stays aligned with its neighbors. */
      .planHeader.mostPopular {
        margin-top: -38px;
        color: ${COLORS.coeblue4};
        background: ${COLORS.coeblue12};
      }

      .planDetails {
        text-align: left;

        /* The self-serve AI Dashboards card layout is fully centered, per design. */
        &.centered {
          text-align: center;
        }
      }

      .planTitle,
      .planPrice {
        font-size: 24px;
        font-weight: bold;

        &.planPrice {
          margin: 10px 0px 0px;
          font-size: 32px;

          &.volDiscount {
            font-size: 16px;
            margin: 22px 0px 0px;
          }
        }
      }

      .ctaBtn {
        cursor: pointer;
        margin: 16px 0px 16px;
        padding: 0px 25px;
        font-weight: bold;
        height: 40px;
        width: 100%;

        &.downgrade {
          box-shadow: none;
          background: white;
          border-color: white;
          color: ${COLORS.coeblue4};

          &:hover {
            border-color: ${COLORS.black9};
            background: ${COLORS.black9};
          }
        }

        &.selected {
          pointer-events: none;
          box-shadow: none;
          background: white;
          border-color: white;
          color: ${COLORS.black};
        }
      }
    }

    &.otherUsers {
      flex: 1;
      height: 582px;
      margin-left: 30px;

      h3 {
        font-weight: bold;
        font-size: 18px;
      }

      a {
        font-weight: bold;
      }

      .userList {
        margin: 0px auto 40px;

        a {
          display: block;
          margin-top: 5px;
          color: ${COLORS.coeblue4};
        }
      }
    }

    .row {
      font-weight: bold;
      margin: 16px 0px;
    }

    .underline {
      border-bottom: 1px dotted ${COLORS.black45};
      cursor: pointer;
    }
  }
`;

const SelectorWrapV2 = styled(SelectorWrap)`
  .column {
    &.labels {
      min-height: 0;
      justify-content: flex-start;
      flex-shrink: 0;
    }

    .row {
      font-weight: 500;
    }

    .row.header {
      font-weight: 700;
      padding-bottom: 8px;
      margin-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      margin-top: 24px;
    }

    /* First matrix row (Builders), ruled off from the sections below. */
    .row.builders {
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }

    .planBox .planDetails .planPrice {
      margin: 0;
    }
  }

  .column.plan {
    min-width: 200px;
  }

  .column.otherUsers {
    height: 1410px;
    padding-top: 8px;
  }

  /* Self-serve AI Dashboards plan cards, per the Figma "Pricing Toggle" design: 240px cards
     with 12px gutters, 40px ribbon, 28px titles, 40px prices, 180px CTAs and 40px matrix rows. */
  &.selfServe {
    h2 {
      font-size: 26px;
    }

    .column.labels {
      width: ${SELF_SERVE_LABELS_WIDTH}px;
    }

    .column.labels .row,
    .column.plan .row {
      margin: 0;
      padding: 4px 0px;
      font-size: 16px;
      line-height: 32px;

      &.header {
        margin: 31px 0px 1px;
        padding: 0px 0px 9px;
        line-height: 20px;
      }

      &.builders + .header {
        margin-top: 12px;
      }
    }

    /* Fixed 327px side column (Billing Frequency, Cancel Plan, FAQ), per design. */
    .column.otherUsers {
      flex: 0 0 ${SELF_SERVE_SIDE_COLUMN_WIDTH}px;
      width: ${SELF_SERVE_SIDE_COLUMN_WIDTH}px;
      margin-left: 40px;
      padding-top: 0;
    }

    .column.plan {
      width: 252px;

      .planHeader,
      .planBox {
        margin-left: 12px;
      }

      /* min-height, not height: single-line ribbons render at exactly 40px, while the
         multi-line free-trial countdown (copy + progress bar) can still grow. */
      .planHeader {
        min-height: 40px;
        padding: 8px 16px;
        font-size: 16px;
        line-height: 24px;
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
      }

      &.current .planHeader,
      .planHeader.mostPopular {
        margin-top: -40px;
      }

      .planBox {
        padding: 25px 18px 22px;
        border-radius: 20px;

        /* Card under a ribbon joins it flush; only the ribbon keeps the top corners. */
        &.noTopCurve {
          border-top-left-radius: 0px;
          border-top-right-radius: 0px;
        }
      }

      .planTitle {
        font-size: 28px;
        line-height: 32px;
      }

      /* Fixed 32px row so the 40px price and 14px unit (baseline-aligned) don't grow it. */
      .priceRow {
        display: flex;
        align-items: baseline;
        justify-content: center;
        gap: 10px;
        height: 32px;
        margin: 22px 0px 18px;
        font-size: 14px;
        font-weight: 500;
        line-height: 32px;
      }

      .planDetails .planPrice {
        font-size: 40px;
        line-height: 32px;
      }

      .letsChat {
        align-items: center;
        justify-content: center;
        gap: 10px;
        font-size: 28px;
        font-weight: bold;
        line-height: 32px;
        letter-spacing: -1px;
      }

      .ctaBtn {
        display: block;
        width: 180px;
        margin: 19px auto 16px;
        padding: 0px;
        font-size: 16px;
        border-radius: 5px;
      }
    }

    .intervalSwitch {
      gap: 12px;
    }

    .savingsTag {
      padding: 2px 4px;
      font-size: 12px;
      font-weight: 600;
      line-height: normal;
      border: none;
      background: ${COLORS.greenBg5};
      color: ${COLORS.positiveGreen};
    }
  }
`;

const FreePlanBox = styled.div<{ $selfServe?: boolean }>`
  background: ${COLORS.black4};
  margin: 16px 0px 24px ${FIRST_COLUMN_WIDTH + 10}px;
  padding: 16px 24px;
  box-sizing: border-box;
  border-radius: 8px;
  color: ${COLORS.black65};
  align-items: center;
  width: 680px;
  display: flex;
  gap: 24px;

  h2 {
    font-size: 24px;
    font-weight: bold;
    flex: 1 0 auto;
  }

  div {
    font-size: 12px;
    font-weight: bold;
    line-height: 1.25;
  }

  ${({ $selfServe }) =>
    $selfServe
      ? `
        margin: 12px 0px 24px ${SELF_SERVE_LABELS_WIDTH + 12}px;
        width: 744px;
        padding: 16px 20px;
        border-radius: 20px;

        h2 {
          font-size: 26px;
        }

        div {
          line-height: 14px;
          color: ${COLORS.black45};
        }
      `
      : ""}
`;

// Amber "you'll lose access to" box used by the redesigned downgrade / remove-user
// confirmations (Dashboard Pricing Updates).
const LoseAccessBox = styled.div`
  background: ${COLORS.yellowBg2};
  border-radius: 8px;
  padding: 12px 16px;
  margin: 12px 0px;
  color: ${COLORS.black85};

  .loseHeader {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    margin-bottom: 8px;

    .anticon {
      color: ${COLORS.orange2};
    }
  }

  ul {
    margin: 0px;
    /* Bullets sit a tab-width inside the box, indented past the header icon. */
    padding-left: 40px;
  }

  li {
    margin: 4px 0px;
  }
`;

const PricingSummaryWrap = styled.div`
  background: ${COLORS.coeblue11};
  padding: 8px 24px;
  border-radius: 8px;
  margin: auto;
  font-size: 14px;
  margin-bottom: 16px;

  h3 {
    font-weight: bold;
    margin: 4px 0px 12px;
  }
`;

export const DomainFamilyList = styled.ul`
  margin: 7px 0px 0px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;

  li {
    font-size: 14px;
    line-height: 7px;
    letter-spacing: 0px;
    display: flex;
    align-items: flex-start;
    margin-bottom: 15px;
    padding-right: 80px;

    &:last-child {
      margin-bottom: 10px;
    }

    svg {
      margin-right: 9px;
      margin-top: 5px;
    }

    > div {
      flex: 1;
    }
  }
`;

// Sits in the labels column's empty top band so it never displaces the plan cards.
// FIRST_COLUMN_WIDTH is sized to fit this bar; widen it if the labels or copy grow.
const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-top: 32px;
`;

const IntervalLabel = styled(SwitchLabel)<{ $selfServe?: boolean }>`
  font-size: 16px;

  ${({ $selfServe, active }) =>
    $selfServe
      ? `
        font-size: 14px;
        color: ${active ? COLORS.coeblue4 : COLORS.black50};
      `
      : ""}
`;

const SavingsTag = styled(Tag)`
  margin: 0;
  font-weight: 700;
  border-radius: 4px;
`;

const LinkWithIcon = styled.a`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ConfirmCheckbox = styled(Checkbox)`
  && {
    margin: 0px 0px 0px;
  }
`;

// Slot for the AI Dashboards row: every plan column renders it whenever the offer is shown,
// at the SAME height, so all card headers grow by the same amount — the feature-matrix rows
// below align purely by column height, and the labels column offsets itself with a margin
// estimated from these constants (then corrected by measurement). Collapsed = the toggle row
// only; expanded = toggle row + the 3-line price detail shown while the add-on is on.
const WEB_DASHBOARD_SLOT_HEIGHT = 28;
const WEB_DASHBOARD_SLOT_EXPANDED_HEIGHT = 82;
const WebDashboardSlot = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  display: flex;
  flex-direction: column;
  /* Top-aligned so the AI Dashboards row sits on the same line in every card. */
  justify-content: flex-start;

  .slotMuted {
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: ${COLORS.black50};
    text-align: center;
  }
`;

// Fresh-checkout variant (flow 1 of the Oct 23 mocks): each card shows the add-on row as a
// white box filling the slot. Collapsed = one line; expanded = the Pro box's add-on price below
// its toggle.
const WEB_DASHBOARD_BOX_HEIGHT = 42;
const WEB_DASHBOARD_BOX_EXPANDED_HEIGHT = 72;
const WebDashboardBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  background: white;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: ${COLORS.black85};
  text-align: center;

  &.muted {
    color: ${COLORS.black45};
  }

  &.addon {
    text-align: left;
  }

  .boxToggleRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-weight: 600;
    white-space: nowrap;
  }

  .boxPrice {
    margin-top: 6px;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }
`;

// Fixed-height plan description under each card title (the design shows one for every
// plan); fixed so all three card headers grow identically and the matrix stays aligned.
const PLAN_DESCRIPTION_HEIGHT = 40;
const PlanDescription = styled.div`
  height: ${PLAN_DESCRIPTION_HEIGHT}px;
  padding-top: 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  color: ${COLORS.black85};
  text-align: center;
`;

const WebDashboardAddonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  background: white;
  border-radius: 8px;
  padding: 6px 12px;
  margin: 0;

  /* Stacked variant (toggle + price rows): no white box, centered like the card, no padding */
  &.stacked {
    flex-direction: column;
    align-items: center;
    gap: 19px;
    background: transparent;
    border-radius: 0;
    padding: 0;
  }

  .addonMain {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
  }

  .addonTitle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: ${COLORS.black85};
    white-space: nowrap;
  }

  .addonPrice {
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    color: ${COLORS.gray900};
    text-align: center;
    white-space: nowrap;

    .addonPriceMain {
      font-weight: 600;
    }
  }

  &.disabled {
    .addonTitle {
      color: ${COLORS.black45};
    }
  }

  a {
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.coeblue4};
  }
`;

const NewBadge = styled.span`
  background: ${COLORS.yellowBadge};
  color: ${COLORS.black85};
  font-size: 10px;
  font-weight: 600;
  line-height: normal;
  border-radius: 4px;
  padding: 2px 4px;
`;

export const PlanSelectorV2: React.FC = () => {
  const track = useTrack();

  const { value: enableGmailDiscountTake50Coeff } = useFeatureGate(
    FeatureFlag.EnableGmailDiscountTake50coeff
  );

  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );

  // The labels column renders separately from the plan cards, so its feature rows must be
  // pushed down to line up with the first matrix row inside the cards. The card header height
  // varies with runtime state (discount banner, current-plan header, feature flags, copy), so
  // the hardcoded margin is only a first-paint estimate — after layout we measure both first
  // header rows and correct any remaining drift.
  const labelsMatrixHeaderRef = useRef<HTMLDivElement>(null);
  const planMatrixHeaderRef = useRef<HTMLDivElement>(null);
  const [labelsOffsetAdjust, setLabelsOffsetAdjust] = useState<number>(0);

  const alignLabelsToPlanMatrix = useCallback(() => {
    const labelsHeader = labelsMatrixHeaderRef.current;
    const planHeader = planMatrixHeaderRef.current;
    if (!labelsHeader || !planHeader) {
      return;
    }
    const delta = Math.round(
      planHeader.getBoundingClientRect().top - labelsHeader.getBoundingClientRect().top
    );
    if (delta !== 0) {
      setLabelsOffsetAdjust(prev => prev + delta);
    }
  }, []);

  useLayoutEffect(() => {
    alignLabelsToPlanMatrix();
  });

  useEffect(() => {
    window.addEventListener("resize", alignLabelsToPlanMatrix);
    document.fonts?.ready.then(alignLabelsToPlanMatrix).catch(() => undefined);
    return () => window.removeEventListener("resize", alignLabelsToPlanMatrix);
  }, [alignLabelsToPlanMatrix]);

  const {
    billingMetadata,
    basePath,
    domain,
    selectedBillingInterval,
    setBillingInterval,
    free_trial_expiration_dt,
    free_trial_total_days,
    setDataSources,
    enableSubscriptionUpdate,
    premiumConnectorsUsed,
    setPlanType,
    userSubscription,
    usersInCurrentPlan,
    updateSubscription,
    setUsers,
    setContactSupportInfoBox,
    syncBilling,
    currentUserEmail,
    includeWebDashboards,
    setIncludeWebDashboards,
    eventFrom,
    isManager,
    currentUserPlanManager,
  } = useContext(BillingContext) as BContext;

  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  useEffect(() => {
    document.title = "Choose your plan - Coefficient Workspace";
  }, []);

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const platform = searchParams.get("platform");

  const history = useHistory();

  const subscriptionDetails = userSubscription?.subscription_details;

  const [showContactSalesModal, setShowContactSalesModal] = useState<boolean>(false);

  // Upgrade CTAs (Sheets gates, the AI Dashboards modal) can land any seat holder here, but a plan
  // change acts on the team's subscription: a subscriber who isn't a plan manager gets the same
  // "ask your plan manager" prompt as Change plan instead of a checkout.
  const isNonManagerSubscriber = Boolean(subscriptionDetails) && !isManager;
  const [showManagerPrompt, setShowManagerPrompt] = useState<boolean>(false);

  const [contactSalesConfig, setContactSalesConfig] = useState<{
    description?: string;
    hideTextarea?: boolean;
    gateProps?: AiDashboardsGateProps;
  }>({});

  const openContactSalesModal = (
    config: { description?: string; hideTextarea?: boolean; gateProps?: AiDashboardsGateProps } = {}
  ) => {
    setContactSalesConfig(config);
    setShowContactSalesModal(true);
  };

  // Branded no-textarea Contact Sales flow (request → "Request Sent" confirmation) for users
  // who can't self-serve the AI Dashboards add-on. The copy doubles as the sales-lead message,
  // so it reflects whether the user is adding to an existing Pro plan or upgrading into one.
  const openAiDashboardsContactSales = () =>
    openContactSalesModal({
      description: webDashboardTooManyDashboards
        ? tooManyDashboardsMessage
        : subscriptionDetails?.plan_type === SubscriptionPlanType.Pro
        ? "To add additional users to your Pro plan, with AI Dashboards, contact sales."
        : "To upgrade to a Pro plan with AI Dashboards, contact sales.",
      hideTextarea: true,
      gateProps: AI_DASHBOARDS_BILLING_PAGE_GATE_PROPS,
    });

  const [showAddWebDashboardModal, setShowAddWebDashboardModal] = useState<boolean>(false);

  const [showTrialDowngradeInfo, setShowTrialDowngradeInfo] = useState<boolean>(false);

  const [showInvalidStarterDowngrade, setShowInvalidStarterDowngrade] = useState<boolean>(false);

  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState<"free" | "starter" | null>(null);

  const [showStarterDowngradePricingSummary, setShowStarterDowngradePricingSummary] =
    useState<boolean>(false);

  const [downgradeLoading, setDowngradeLoading] = useState<boolean>(false);

  const [newPriceSummary, setNewPriceSummary] = useState<null | PricingSummary>(null);

  const plan_type = subscriptionDetails?.plan_type;

  const [newBillingIntervalPricing, setNewBillingIntervalPricing] = useState<PricingSummary | null>(
    null
  );

  const [newBillingInterval, setNewBillingInterval] = useState<SubscriptionBillingInterval | null>(
    null
  );

  const currentPlan = billingMetadata?.plans.find(
    plan =>
      plan.plan_type === plan_type &&
      plan.billing_interval === subscriptionDetails?.billing_interval
  );

  const [userConfirmChangeBillingCycle, setUserConfirmChangeBillingCycle] =
    useState<boolean>(false);

  const [userConfirmChangeBillingCycleHighlight, setUserConfirmChangeBillingCycleHighlight] =
    useState<boolean>(false);

  const [updateBillingIntervalLoading, setUpdateBillingIntervalLoading] = useState<boolean>(false);

  const updateBillingInterval = async () => {
    setUpdateBillingIntervalLoading(true);

    const toBeUpdatedPlan = billingMetadata?.plans.find(
      plan =>
        plan.plan_type === subscriptionDetails?.plan_type &&
        plan.billing_interval === newBillingInterval
    );

    const response = await updateSubscriptionOnServer(
      {
        plan: toBeUpdatedPlan,
        update_type: "modify_subscription",
        total_user_licenses: subscriptionDetails?.max_users,
        selected_data_source_types: subscriptionDetails?.selected_data_source_types,
        selected_user_ids: usersInCurrentPlan.map(u => u.user?.user_id!),
        coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
        subscription_id: subscriptionDetails?.subscription_id,
      },
      newBillingInterval === "annually"
        ? "Billing frequency updated to annually"
        : "Billing frequency updated to monthly"
    );

    setNewBillingInterval(null);

    setUserConfirmChangeBillingCycleHighlight(false);
    setUserConfirmChangeBillingCycle(false);

    setUpdateBillingIntervalLoading(false);

    track(response ? "billing_frequency_update_failed" : "billing_frequency_update_succeeded", {
      selected_num_data_sources: subscriptionDetails?.selected_data_source_types?.length,
      selected_num_users: usersInCurrentPlan.length,
      selected_frequency: toBeUpdatedPlan?.billing_interval,
      selected_plan_type: toBeUpdatedPlan?.plan_type,
    });
  };

  const updateBillingIntervalHandler = () => {
    track("billing_settings_frequency_update_clicked");

    if (!enableSubscriptionUpdate) {
      return setContactSupportInfoBox({ title: "Billing Frequency" });
    }

    const currentBillingInterval = subscriptionDetails?.billing_interval;

    const newBillingInterval = currentBillingInterval === "monthly" ? "annually" : "monthly";

    const planMonthlyPrice: number =
      billingMetadata?.plans.find(
        p => p.plan_type === subscriptionDetails?.plan_type && p.billing_interval === "monthly"
      )?.per_user_plan?.per_item_monthly_price_usd || 0;

    const planAnnualMonthlyPrice: number =
      billingMetadata?.plans.find(
        p => p.plan_type === subscriptionDetails?.plan_type && p.billing_interval === "annually"
      )?.per_user_plan?.per_item_monthly_price_usd || 0;

    const newPricing = getPricingSummary(
      subscriptionDetails?.plan_type,
      newBillingInterval, // billing interval
      planMonthlyPrice, // monthly price for the plan (monthly interval)
      planAnnualMonthlyPrice, // monthly price for the plan (annual interval)
      currentPlan?.per_additional_data_source_addon?.per_item_monthly_price_usd || 0, // addtional data source cost
      subscriptionDetails?.max_users ?? 1, // # of users
      subscriptionDetails?.max_data_source_types ?? 0, // # of data souce
      0, // AI Dashboards addon price (monthly interval)
      0, // AI Dashboards addon price (annual interval)
      false, // include AI Dashboards addon
      billingMetadata?.coupon_discount_percentage
    );

    setPlanType(subscriptionDetails?.plan_type ?? "pro");

    setBillingInterval(newBillingInterval);

    setNewBillingIntervalPricing(newPricing);

    setNewBillingInterval(newBillingInterval);
  };

  const onEnterprisePlanClick = () => {
    track("billing_plan_selected", {
      selected_frequency: selectedBillingInterval,
      selected_plan_type: "enterprise",
    });

    window.open("https://coefficient.io/enterprise-pricing", "_blank");
  };

  // Subscribers get no interval toggle, so their cards follow the subscription's own interval;
  // selectedBillingInterval is prospect shopping state that other flows here mutate.
  const cardBillingInterval = subscriptionDetails?.billing_interval ?? selectedBillingInterval;

  const getPlanData = (planType: SubscriptionPlanType) => {
    if (!billingMetadata) {
      return null;
    }
    const plan = billingMetadata.plans.find(
      p => p.plan_type === planType && p.billing_interval === cardBillingInterval
    );

    return plan;
  };

  const handleManageTeam = () => {
    const searchParams = new URLSearchParams(window.location.search);

    track("billing_manage_team_clicked");

    history.push({
      pathname: `${basePath}/team/manage`,
      search: searchParams.toString(),
    });
  };

  const planClicked = (clickedPlan: SubscriptionPlanType) => {
    if (isNonManagerSubscriber) {
      return setShowManagerPrompt(true);
    }
    if (shouldBlockEnterpriseCheckout(clientFlags, premiumConnectorsUsed, subscriptionDetails)) {
      return openContactSalesModal();
    }

    if (clickedPlan === "starter") {
      if (plan_type === "pro") {
        return downgradeToStarterCheck();
      }

      track("billing_plan_selected", {
        event_from: eventFrom,
        selected_frequency: selectedBillingInterval,
        selected_plan_type: "starter",
      });

      setPlanType("starter");

      history.push({
        pathname: `${basePath}/customize`,
        search: window.location.search,
      });
    } else if (clickedPlan === "pro") {
      // Multi-user / sales-managed domains can't self-serve AI Dashboards, so
      // "Upgrade to Pro" routes through sales instead of the checkout flow.
      // Owning too many dashboards only blocks the add-on, not Pro itself.
      if (
        webDashboardContactSalesOnly &&
        !webDashboardTooManyDashboards &&
        (plan_type === undefined || plan_type === SubscriptionPlanType.Starter)
      ) {
        return openAiDashboardsContactSales();
      }

      if (plan_type === "starter") {
        setBillingInterval(subscriptionDetails?.billing_interval ?? "annually");
        setDataSources(subscriptionDetails?.selected_data_source_types ?? []);
        setUsers(usersInCurrentPlan.map(u => u.user ?? {}));
      }

      track("billing_plan_selected", {
        event_from: eventFrom,
        selected_frequency: selectedBillingInterval,
        selected_plan_type: "pro",
      });

      setPlanType("pro");

      history.push({
        pathname: `${basePath}/customize`,
        search: window.location.search,
      });
    }
  };

  const updateSubscriptionOnServer = async (body, successMessage: string) => {
    const response = await updateSubscription(body);

    if (!response.is_success) {
      message.error(response.error_msg || "Uh-oh. Something went wrong. Please try again");
    } else {
      message.success(successMessage);

      syncBilling();
    }

    return response;
  };

  const downgradeToStarterCheck = () => {
    if (subscriptionDetails?.max_users && subscriptionDetails?.max_users > 1) {
      setShowInvalidStarterDowngrade(true);
    } else {
      setShowDowngradeConfirm("starter");
    }
  };

  const downgradeToStarterShowPricing = () => {
    const starterPlan = billingMetadata?.plans.find(
      plan =>
        plan.plan_type === "starter" &&
        plan.billing_interval === subscriptionDetails?.billing_interval
    );

    const planMonthlyPrice: number =
      billingMetadata?.plans.find(
        p => p.plan_type === "starter" && p.billing_interval === "monthly"
      )?.per_user_plan?.per_item_monthly_price_usd || 0;

    const planAnnualMonthlyPrice: number =
      billingMetadata?.plans.find(
        p => p.plan_type === "starter" && p.billing_interval === "annually"
      )?.per_user_plan?.per_item_monthly_price_usd || 0;

    const newPriceSummary = getPricingSummary(
      subscriptionDetails?.plan_type,
      subscriptionDetails?.billing_interval ?? "annually", // billing interval
      planMonthlyPrice, // monthly price for the plan (monthly interval)
      planAnnualMonthlyPrice, // monthly price for the plan (annual interval)
      starterPlan?.per_additional_data_source_addon?.per_item_monthly_price_usd || 0, // addtional data source cost
      1, // # of users
      subscriptionDetails?.max_data_source_types ?? 0, // # of data souce
      0, // AI Dashboards addon price (monthly interval)
      0, // AI Dashboards addon price (annual interval)
      false, // include AI Dashboards addon
      billingMetadata?.coupon_discount_percentage
    );

    setPlanType(subscriptionDetails?.plan_type ?? "pro");
    setBillingInterval(subscriptionDetails?.billing_interval ?? "annually");
    setNewPriceSummary(newPriceSummary);
    setShowStarterDowngradePricingSummary(true);
  };

  const downgradeToStarter = async () => {
    setDowngradeLoading(true);
    const starterPlan = billingMetadata?.plans.find(
      plan =>
        plan.plan_type === "starter" &&
        plan.billing_interval === subscriptionDetails?.billing_interval
    );

    const response = await updateSubscriptionOnServer(
      {
        plan: starterPlan,
        update_type: "modify_subscription",
        total_user_licenses: 1,
        selected_data_source_types: subscriptionDetails?.selected_data_source_types,
        selected_user_ids: usersInCurrentPlan.map(u => u.user?.user_id),
        coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
        subscription_id: subscriptionDetails?.subscription_id,
      },
      "Your plan will be downgraded to Starter"
    );
    setShowStarterDowngradePricingSummary(false);
    setDowngradeLoading(false);

    track("billing_plan_changed", {
      from_plan_type: "starter",
      to_plan_type: "pro",
    });

    if (response.is_success) {
      history.push({
        pathname: `${basePath}/subscriptions`,
        search: window.location.search,
      });
    }
  };

  const handleViewPlan = () => {
    history.push({
      pathname: `${basePath}/subscriptions`,
      search: window.location.search,
    });
  };

  const fractionalDaysLeft = free_trial_expiration_dt
    ? getFreeTrialNumFractionalDaysLeft(free_trial_expiration_dt)
    : null;
  const percent = 100 - ((fractionalDaysLeft ?? 0) / (free_trial_total_days ?? 0)) * 100;

  // Start the progress bar visually at 15% (instead of starting out empty if trial just started)
  const clampMinPercent = 50;
  const clampMaxPercent = 100;
  const scaledPercent = Math.max(clampMinPercent, Math.min(clampMaxPercent, percent));

  // Quiet fine print under the downgrade confirmations (no box, no bold, softer copy).
  const DowngradeWarning = (
    <div style={{ fontSize: 12, lineHeight: "16px", color: COLORS.black45, marginTop: "12px" }}>
      Downgrading your plan doesn&apos;t cancel your subscription.{" "}
      <CancelPlanHelpText
        subscriptionDetails={subscriptionDetails}
        clientFlags={clientFlags}
        userEmail={currentUserEmail}
        featureEnabled={true}
        compact
      />
    </div>
  );

  const paidPlanTypes: Array<SubscriptionPlanType> = [
    SubscriptionPlanType.Enterprise,
    SubscriptionPlanType.Pro,
    SubscriptionPlanType.Plus,
    SubscriptionPlanType.Starter,
  ];

  const starterSeatPrice = getPlanCardSeatPrice(
    getPlanData("starter")?.per_user_plan?.per_item_monthly_price_usd,
    billingMetadata?.seat_price_carried_by_addon,
    59
  );
  const proSeatPrice = getPlanCardSeatPrice(
    getPlanData("pro")?.per_user_plan?.per_item_monthly_price_usd,
    billingMetadata?.seat_price_carried_by_addon,
    119
  );
  const starterPlanPrice = starterSeatPrice.type === "price" ? starterSeatPrice.value : 0;
  const proPlanPrice = proSeatPrice.type === "price" ? proSeatPrice.value : 0;
  // Pro card ribbon: only pitched to users not yet on a paid plan (trial users see the
  // trial-countdown header instead).
  const showMostPopularHeader =
    enableSelfServeAiDashboards &&
    plan_type !== "free_trial" &&
    !paidPlanTypes.includes(plan_type as SubscriptionPlanType);
  const shouldShowGmailDiscount = Boolean(domain === "gmail.com" && enableGmailDiscountTake50Coeff);

  const webDashboardEligibility = enableSelfServeAiDashboards
    ? billingMetadata?.web_dashboard_eligibility
    : undefined;
  const hasWebDashboardAddon = webDashboardEligibility === WebDashboardEligibility.AlreadyHasAddon;
  const showWebDashboardOffer = shouldShowWebDashboardOffer(
    enableSelfServeAiDashboards,
    billingMetadata
  );

  // Bundle toggle price follows the same interval as the plan cards.
  const webDashboardTogglePrice =
    getPlanData("pro")?.web_dashboard_addon?.per_item_monthly_price_usd ?? 0;

  // Add-on offer price for an existing Pro subscription follows its billing interval.
  const webDashboardAddonPrice =
    billingMetadata?.plans.find(
      p => p.plan_type === "pro" && p.billing_interval === subscriptionDetails?.billing_interval
    )?.web_dashboard_addon?.per_item_monthly_price_usd ?? 0;

  const webDashboardContactSalesOnly =
    webDashboardEligibility === WebDashboardEligibility.MultiSeat ||
    webDashboardEligibility === WebDashboardEligibility.SalesManaged ||
    webDashboardEligibility === WebDashboardEligibility.DomainHasDashboardPack ||
    // Any other live subscription in the domain means a team setup — sales only.
    webDashboardEligibility === WebDashboardEligibility.DomainHasOtherSubscriptions ||
    // Already owns more dashboards than the self-serve add-on includes.
    webDashboardEligibility === WebDashboardEligibility.TooManyDashboards ||
    // Sales is already working an open HubSpot deal with this domain.
    webDashboardEligibility === WebDashboardEligibility.OpenSalesDeal;

  const webDashboardTooManyDashboards =
    webDashboardEligibility === WebDashboardEligibility.TooManyDashboards;
  const tooManyDashboardsMessage = getTooManyDashboardsMessage(
    billingMetadata?.web_dashboard_active_count
  );

  // Toggle shown for fresh checkouts (interactive) and Starter→Pro upgrades (informational).
  const showWebDashboardToggle =
    webDashboardEligibility === WebDashboardEligibility.EligibleWithPlan &&
    (!subscriptionDetails || subscriptionDetails.plan_type === SubscriptionPlanType.Starter) &&
    webDashboardTogglePrice > 0;

  // The price detail (and the taller slot that fits it) shows only while the add-on is on. The
  // Pro headline stays the plan price alone; the add-on's price is that detail line.
  const webDashboardSlotExpanded =
    (showWebDashboardToggle && includeWebDashboards) || hasWebDashboardAddon;
  // A fresh checkout (no subscription, or Starter → Pro) gets the boxed cards and no Builders row;
  // existing subscriptions keep the stacked toggle + price detail.
  const webDashboardBoxed = showWebDashboardOffer && showWebDashboardToggle;
  const showBuildersRow = showWebDashboardOffer && !webDashboardBoxed;
  const webDashboardSlotHeight = !showWebDashboardOffer
    ? 0
    : webDashboardBoxed
    ? webDashboardSlotExpanded
      ? WEB_DASHBOARD_BOX_EXPANDED_HEIGHT
      : WEB_DASHBOARD_BOX_HEIGHT
    : webDashboardSlotExpanded
    ? WEB_DASHBOARD_SLOT_EXPANDED_HEIGHT
    : WEB_DASHBOARD_SLOT_HEIGHT;

  const webDashboardPriceDetail = (price: number) => (
    <div className="addonPrice">
      <div className="addonPriceMain">+ ${price} builder/month</div>
      <div>{WEB_DASHBOARD_MAX_PER_BUILDER}</div>
      <div>(viewers free)</div>
    </div>
  );

  const WebDashboardProPlanRow = (() => {
    if (hasWebDashboardAddon) {
      // Already subscribed: the toggle reads as on but is not interactive.
      return (
        <WebDashboardAddonRow className="stacked">
          <div className="addonMain">
            <Switch size="small" checked style={{ cursor: "default" }} />
            <div className="addonTitle">
              AI Dashboards
              <NewBadge>NEW</NewBadge>
            </div>
          </div>
          {webDashboardTogglePrice > 0 && webDashboardPriceDetail(webDashboardTogglePrice)}
        </WebDashboardAddonRow>
      );
    }

    if (showWebDashboardToggle) {
      return (
        <WebDashboardBox className="addon">
          <div className="boxToggleRow">
            AI Dashboards Add-on
            <Switch
              size="small"
              checked={includeWebDashboards}
              onChange={checked => {
                track("billing_ai_dashboards_toggled", {
                  enabled: checked,
                  selected_frequency: selectedBillingInterval,
                  event_from: eventFrom,
                });
                setIncludeWebDashboards(checked);
              }}
            />
          </div>
          {includeWebDashboards && <div className="boxPrice">${webDashboardTogglePrice}</div>}
        </WebDashboardBox>
      );
    }

    if (webDashboardContactSalesOnly) {
      // Ineligible users see the toggle off; flipping it routes to Contact Sales (the toggle
      // itself never turns on).
      return (
        <WebDashboardAddonRow className="stacked">
          <div className="addonMain">
            <Switch size="small" checked={false} onChange={() => openAiDashboardsContactSales()} />
            <div className="addonTitle">
              AI Dashboards
              <NewBadge>NEW</NewBadge>
            </div>
          </div>
        </WebDashboardAddonRow>
      );
    }

    if (
      webDashboardEligibility === WebDashboardEligibility.Eligible &&
      webDashboardAddonPrice > 0
    ) {
      // Same toggle as the other states; flipping it on opens the purchase confirmation, and it
      // only reads as on once the add-on is bought (eligibility becomes AlreadyHasAddon).
      return (
        <WebDashboardAddonRow className="stacked">
          <div className="addonMain">
            <Switch
              size="small"
              checked={showAddWebDashboardModal}
              onChange={checked => {
                if (checked && isNonManagerSubscriber) {
                  setShowManagerPrompt(true);
                } else if (checked) {
                  track("billing_ai_dashboards_toggled", {
                    enabled: true,
                    selected_frequency: subscriptionDetails?.billing_interval,
                    event_from: eventFrom,
                  });
                  setShowAddWebDashboardModal(true);
                }
              }}
            />
            <div className="addonTitle">
              AI Dashboards
              <NewBadge>NEW</NewBadge>
            </div>
          </div>
        </WebDashboardAddonRow>
      );
    }

    return null;
  })();

  // Billing Frequency / Cancel Plan cards; the self-serve design uses tighter 12px/20px padding.
  const sideCardStyle: React.CSSProperties = enableSelfServeAiDashboards
    ? { borderRadius: "6px", background: COLORS.black2, padding: "12px 20px 12px 21px" }
    : { borderRadius: "5px", background: COLORS.black2, padding: "16px" };

  const PerPlanDetails = (
    <SelectorWrapV2 className={clsx({ selfServe: enableSelfServeAiDashboards })}>
      <FlexColumn gap={0}>
        <TitleRow>
          <Title>
            <Typography>
              <h2>Choose your plan</h2>
            </Typography>
          </Title>

          <FlexColumn style={{ marginBottom: "16px" }}>
            {shouldShowGmailDiscount ? (
              <Banner style={{ backgroundColor: COLORS.yellow }}>
                <BannerContent>
                  <GiftIcon />
                  <Typography fontWeight={600}>
                    For a limited time, get 50% off your first year on Starter & Pro plans!
                  </Typography>
                </BannerContent>
              </Banner>
            ) : null}

            {platform === "excel_addon" ? (
              <Banner>
                <BannerContent>
                  <Excel height={28} width={28} />

                  <Typography fontWeight={600}>
                    Some features are not supported in Excel yet but are coming soon!
                  </Typography>
                </BannerContent>

                <BannerAction target="_blank" href="https://coefficient.io/pricing#excel-support">
                  Learn more
                </BannerAction>
              </Banner>
            ) : null}
          </FlexColumn>
        </TitleRow>

        {/* Columns must be top-aligned: with FlexRow's default centering, any difference in
            total column height re-centers the labels column and shifts every label off its
            matrix row. */}
        <FlexRow gap={0} alignItems="flex-start">
          <div className="column labels">
            {/* Landing-page control only; subscribers use the Billing Frequency card instead.
                Trials have no Subscription row, so !subscriptionDetails covers them too. */}
            {!subscriptionDetails ? (
              <SwitchContainer
                className="intervalSwitch"
                onClick={() =>
                  setBillingInterval(selectedBillingInterval === "monthly" ? "annually" : "monthly")
                }
              >
                <IntervalLabel
                  $selfServe={enableSelfServeAiDashboards}
                  active={selectedBillingInterval === "monthly"}
                >
                  Monthly
                </IntervalLabel>

                <Switch active checked={selectedBillingInterval === "annually"} />

                <IntervalLabel
                  $selfServe={enableSelfServeAiDashboards}
                  active={selectedBillingInterval === "annually"}
                >
                  Annual
                </IntervalLabel>

                {selectedBillingInterval === "annually" ? (
                  <SavingsTag className="savingsTag" color="green">
                    Save 17%
                  </SavingsTag>
                ) : null}
              </SwitchContainer>
            ) : null}

            <div
              style={{
                paddingBottom: "20px",
                // First-paint estimate of the plan cards' header height (192px base, plus the
                // AI Dashboards slot and description when the flag is on). labelsOffsetAdjust
                // then corrects it to the measured position of the cards' first matrix row.
                marginTop: `${
                  (enableSelfServeAiDashboards
                    ? 192 + webDashboardSlotHeight + PLAN_DESCRIPTION_HEIGHT
                    : 192) + labelsOffsetAdjust
                }px`,
              }}
            >
              {/* Builders row: Pro is billed per builder. */}
              {showBuildersRow && <div className="row builders">Builders</div>}
              <div className="row header" ref={labelsMatrixHeaderRef}>
                Import Data To Sheet
              </div>
              <div className="row">
                <FlexContainer>
                  <Tooltip title="The number of distinct data source systems you can connect to. For example, Salesforce and Snowflake would be 2 sources.">
                    <span className="underline">Data Sources</span>
                  </Tooltip>

                  <a
                    href="https://coefficient.io/integrations"
                    target="_blank"
                    style={{ fontSize: 12, fontWeight: 700, marginTop: 1 }}
                  >
                    See all
                  </a>
                </FlexContainer>
              </div>
              <div className="row">
                <Tooltip title="The number of distinct accounts you can connect within the same source. For example, if you use Instagram and have 3 different accounts. For many sources, you may only have 1 account.">
                  <span className="underline">Accounts Per Source</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="The maximum number of rows for importing is set individually for each import. You can create multiple imports, each with its own limit. Rows exceeding this limit will be excluded.">
                  <span className="underline">Import Size</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="The monthly limit for refreshing imports includes both manual and scheduled runs and it's cumulative across all your created imports.">
                  <span className="underline">Refresh Limit</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="The frequency at which you can schedule automated refreshes of your data imports.">
                  <span className="underline">Auto Data Refresh</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="Automated snapshots of your data that get copied into a new tab within the same spreadsheet.">
                  <span className="underline">Data Snapshots</span>
                </Tooltip>
              </div>

              {enableSelfServeAiDashboards && (
                <>
                  <div className="row header">
                    AI Dashboards
                    <NewBadge style={{ marginLeft: 8, fontSize: 12 }}>NEW</NewBadge>
                  </div>
                  <div className="row"># of dashboards</div>
                  <div className="row">Unlimited viewers</div>
                  <div className="row">Custom themes, logo</div>
                </>
              )}

              <div className="row header">Export Data From Sheet</div>
              <div className="row">
                <Tooltip title="The maximum number of rows for exporting is set individually for each export. You can create multiple exports, each with its own limit. Rows exceeding this limit will be excluded.">
                  <span className="underline">Export Size</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="The monthly limit for running exports includes both manual and scheduled runs and it's cumulative across all your created exports.">
                  <span className="underline">Export Runs Limit</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="The frequency at which you can schedule automated refreshes of your data exports.">
                  <span className="underline">Auto Data Export</span>
                </Tooltip>
              </div>

              <div className="row header">Data Alerts</div>
              <div className="row">
                <Tooltip title="The monthly limit on number of alerts sent via Slack and email.">
                  <span className="underline">Slack & Email Alerts</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="Send alerts dynamically using the email address column in a table of data.">
                  <span className="underline">Dynamic Recipients</span>
                </Tooltip>
              </div>

              <div className="row header">AI Features</div>
              <div className="row">
                <LinkWithIcon href="https://coefficient.io/gpt-google-sheets" target="_blank">
                  <span
                    style={{ color: COLORS.black85, borderBottom: `1px solid ${COLORS.black45}` }}
                  >
                    On Sheet Functions
                  </span>
                  <NewWindow3 fill={COLORS.black45} />
                </LinkWithIcon>
              </div>
              <div className="row">
                <LinkWithIcon href="https://coefficient.io/ai" target="_blank">
                  <span
                    style={{ color: COLORS.black85, borderBottom: `1px solid ${COLORS.black45}` }}
                  >
                    SQL, Chart & Pivot Builders
                  </span>
                  <NewWindow3 fill={COLORS.black45} />
                </LinkWithIcon>
              </div>
              <div className="row">
                <Tooltip title="The monthly limit on number of OpenAI API calls across all AI features.">
                  <span className="underline">OpenAI API Calls</span>
                </Tooltip>
              </div>

              <div className="row header">Collaboration & Admin</div>
              <div className="row">
                <Tooltip title="Allows multiple users to refresh your imports and collaborate in a common workspace.">
                  <span className="underline">Multi-User Support</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="Ability to share your data source connections with other users in your workspace.">
                  <span className="underline">Shared Connections</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="Ability to save and share reusable import templates with users in your workspace.">
                  <span className="underline">Shared Import Templates</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="Centralized admin controls and visibility across your workspace.">
                  <span className="underline">Admin Controls</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="Configure your own custom SSO login provider like Okta.">
                  <span className="underline">Custom SSO</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="Manage billing for multiple users across your workspace.">
                  <span className="underline">Consolidated Billing</span>
                </Tooltip>
              </div>
              <div className="row">
                <Tooltip title="We’ll work with you to go through a full security review process and MSA.">
                  <span className="underline">Security Review & MSA</span>
                </Tooltip>
              </div>

              <div className="row header">Support</div>
              <div className="row">Help Center Access</div>
              <div className="row">Support Channel</div>
            </div>
          </div>

          <div
            className={clsx("column plan starter", {
              current: plan_type === SubscriptionPlanType.Starter,
            })}
          >
            {plan_type === "starter" && <div className="planHeader">Current plan</div>}
            <div className={`planBox ${plan_type === "starter" ? "noTopCurve" : ""}`}>
              <div className={clsx("planDetails", { centered: enableSelfServeAiDashboards })}>
                {starterSeatPrice.type === "included" ? (
                  <React.Fragment>
                    <div className="planTitle">Starter</div>
                    <div style={{ marginBottom: "10px" }}>
                      <span className="planPrice">Included</span>
                      <div style={{ fontSize: "12px", color: COLORS.black45 }}>
                        Billed through your AI Dashboards subscription
                      </div>
                    </div>
                  </React.Fragment>
                ) : shouldShowGmailDiscount ? (
                  <React.Fragment>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="planTitle">Starter</div>
                      <Tag
                        style={{
                          background: COLORS.black4,
                          borderColor: COLORS.yellow,
                          fontWeight: "bold",
                          fontSize: "16px",
                          padding: "0 2px",
                          margin: 0,
                        }}
                      >
                        50% OFF
                      </Tag>
                    </div>
                    {enableSelfServeAiDashboards && (
                      <PlanDescription>
                        For solo builders ready to operationalize their manual workflows
                      </PlanDescription>
                    )}
                    <div>
                      <span className="planPrice" style={{ marginRight: "6px" }}>
                        ${Math.floor(starterPlanPrice * 0.5).toFixed(0)}
                      </span>{" "}
                      / month
                    </div>
                    <div>
                      <del>Regular price: ${starterPlanPrice}</del>
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <div className="planTitle">Starter</div>
                    {enableSelfServeAiDashboards && (
                      <PlanDescription>
                        For solo builders ready to operationalize their manual workflows
                      </PlanDescription>
                    )}
                    {enableSelfServeAiDashboards ? (
                      <div className="priceRow">
                        <span className="planPrice">${starterPlanPrice}</span>/ mo
                      </div>
                    ) : (
                      <div style={{ marginBottom: "10px" }}>
                        <span className="planPrice">${starterPlanPrice}</span> / month
                      </div>
                    )}
                  </React.Fragment>
                )}
              </div>

              {showWebDashboardOffer && (
                <WebDashboardSlot $height={webDashboardSlotHeight}>
                  {webDashboardBoxed ? (
                    <WebDashboardBox className="muted">AI Dashboards Not Available</WebDashboardBox>
                  ) : (
                    <div className="slotMuted">AI Dashboards Not Available</div>
                  )}
                </WebDashboardSlot>
              )}

              {plan_type !== SubscriptionPlanType.Starter ? (
                <Button
                  className={clsx("ctaBtn", {
                    downgrade: plan_type !== undefined,
                  })}
                  type="primary"
                  onClick={() => planClicked("starter")}
                >
                  {`${plan_type === undefined ? "Upgrade to Starter" : "Downgrade"}`}
                </Button>
              ) : plan_type === SubscriptionPlanType.Starter ? (
                <Button
                  className={clsx("ctaBtn", {
                    downgrade: true,
                  })}
                  type="primary"
                  onClick={handleViewPlan}
                >
                  {`View plan`}
                </Button>
              ) : (
                <div style={{ height: "40px" }} />
              )}

              {showBuildersRow && <div className="row builders">1</div>}
              <div className="row header" ref={planMatrixHeaderRef}>
                &nbsp;
              </div>
              <div className="row">3</div>
              <div className="row">1</div>
              <div className="row">5,000 rows</div>
              <div className="row">500 refreshes / month</div>
              <div className="row">Daily</div>
              <div className="row">
                <Tick />
              </div>

              {enableSelfServeAiDashboards && (
                <>
                  <div className="row header">&nbsp;</div>
                  <div className="row">-</div>
                  <div className="row">-</div>
                  <div className="row">-</div>
                </>
              )}

              <div className="row header">&nbsp;</div>
              <div className="row">500 rows</div>
              <div className="row">500 runs / month</div>
              <div className="row">Daily</div>

              <div className="row header">&nbsp;</div>
              <div className="row">100 alerts / month</div>
              <div className="row">-</div>

              <div className="row header">&nbsp;</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">50K / month</div>

              <div className="row header">&nbsp;</div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>

              <div className="row header">&nbsp;</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">Chat, Email</div>
            </div>
          </div>

          <div
            className={clsx("column plan pro", {
              current: plan_type === SubscriptionPlanType.Pro || plan_type === undefined,
            })}
          >
            {showMostPopularHeader && <div className="planHeader mostPopular">Most Popular</div>}
            {(plan_type === "pro" || plan_type === "free_trial") && (
              <div className="planHeader">
                {plan_type === "free_trial" && fractionalDaysLeft === null && (
                  <>
                    You are on a Free Trial
                    <br />
                    of the Pro Plan
                    <br />
                    <a className="underline" href="#">
                      Learn more
                    </a>
                  </>
                )}

                {plan_type === "free_trial" &&
                  fractionalDaysLeft !== null &&
                  fractionalDaysLeft >= 0 && (
                    <>
                      {Math.ceil(fractionalDaysLeft)} day
                      {pluralize(Math.ceil(fractionalDaysLeft))} left
                      <br />
                      in your free trial
                      <br />
                      <Progress
                        percent={scaledPercent}
                        showInfo={false}
                        size="small"
                        strokeColor={COLORS.yellow}
                      />
                    </>
                  )}

                {plan_type === "pro" && <>Current plan</>}
              </div>
            )}
            <div
              className={`planBox ${
                ["pro", "free_trial"].includes(plan_type ?? "") || showMostPopularHeader
                  ? "noTopCurve"
                  : ""
              }`}
            >
              <div className={clsx("planDetails", { centered: enableSelfServeAiDashboards })}>
                {proSeatPrice.type === "included" ? (
                  <React.Fragment>
                    <div className="planTitle">Pro</div>
                    <div style={{ marginBottom: "10px" }}>
                      <span className="planPrice">Included</span>
                      <div style={{ fontSize: "12px", color: COLORS.black45 }}>
                        Billed through your AI Dashboards subscription
                      </div>
                    </div>
                  </React.Fragment>
                ) : shouldShowGmailDiscount ? (
                  <React.Fragment>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="planTitle">Pro</div>
                      <Tag
                        style={{
                          background: COLORS.yellow,
                          borderColor: COLORS.yellow,
                          fontWeight: "bold",
                          fontSize: "16px",
                          padding: "0 2px",
                          margin: 0,
                        }}
                      >
                        50% OFF
                      </Tag>
                    </div>
                    {enableSelfServeAiDashboards && (
                      <PlanDescription>
                        For builders that need automation, larger data pulls, and instant answers
                      </PlanDescription>
                    )}
                    <div>
                      <span className="planPrice" style={{ marginRight: "6px" }}>
                        ${Math.floor(proPlanPrice * 0.5).toFixed(0)}
                      </span>{" "}
                      <span>user / month</span>
                    </div>
                    <div>
                      <del>Regular price: ${proPlanPrice}</del>
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <div className="planTitle">Pro</div>
                    {enableSelfServeAiDashboards && (
                      <PlanDescription>
                        For builders that need automation, larger data pulls, and instant answers
                      </PlanDescription>
                    )}
                    <div
                      className={clsx({ priceRow: enableSelfServeAiDashboards })}
                      style={enableSelfServeAiDashboards ? undefined : { marginBottom: "10px" }}
                    >
                      <span className="planPrice">${proPlanPrice}</span> <span>user / month</span>
                    </div>
                  </React.Fragment>
                )}
              </div>

              {showWebDashboardOffer && (
                <WebDashboardSlot $height={webDashboardSlotHeight}>
                  {WebDashboardProPlanRow && (
                    // Names the 5-dashboard limit before anyone flips the toggle.
                    <Tooltip
                      placement="top"
                      title={
                        // The slot is fixed-height (it keeps every card's matrix aligned), so the
                        // over-limit explanation lives here and in the contact-sales modal rather
                        // than as an extra line under the price.
                        webDashboardTooManyDashboards ? (
                          tooManyDashboardsMessage
                        ) : (
                          <>
                            {WEB_DASHBOARD_BILLED_PER_BUILDER}
                            <br />
                            {WEB_DASHBOARD_MAX_PER_BUILDER}
                          </>
                        )
                      }
                    >
                      {WebDashboardProPlanRow}
                    </Tooltip>
                  )}
                </WebDashboardSlot>
              )}

              {plan_type !== SubscriptionPlanType.Pro ? (
                <Button
                  className={clsx("ctaBtn", {
                    downgrade: plan_type === SubscriptionPlanType.Enterprise,
                  })}
                  type="primary"
                  onClick={() => planClicked("pro")}
                >
                  {`${
                    plan_type === undefined || plan_type === SubscriptionPlanType.Starter
                      ? "Upgrade to Pro"
                      : "Downgrade"
                  }`}
                </Button>
              ) : (
                <Button
                  className="ctaBtn"
                  onClick={
                    // Flow 3 (Oct 22 mocks): arriving from an AI Dashboards upgrade CTA on a plan
                    // that can't self-serve the add-on, Manage Licenses offers contact sales.
                    // Everyone else manages licenses as usual.
                    webDashboardContactSalesOnly && isAiDashboardsUpgradeSource(eventFrom)
                      ? openAiDashboardsContactSales
                      : handleManageTeam
                  }
                  type="primary"
                >{`Manage Licenses`}</Button>
              )}

              {showBuildersRow && (
                <div className="row builders">
                  Billed per builder (max {SELF_SERVE_WEB_DASHBOARD_LIMIT})
                </div>
              )}
              <div className="row header">&nbsp;</div>
              <div className="row">6</div>
              <div className="row">3</div>
              <div className="row">Unlimited</div>
              <div className="row">5,000 refreshes / month</div>
              <div className="row">Hourly</div>
              <div className="row">
                <Tick />
              </div>

              {enableSelfServeAiDashboards && (
                <>
                  <div className="row header">&nbsp;</div>
                  <div className="row">5 (as add-on)</div>
                  <div className="row">
                    <Tick />
                  </div>
                  <div className="row">-</div>
                </>
              )}

              <div className="row header">&nbsp;</div>
              <div className="row">Unlimited</div>
              <div className="row">5,000 runs / month</div>
              <div className="row">Hourly</div>

              <div className="row header">&nbsp;</div>
              <div className="row">300 alerts / month</div>
              <div className="row">
                <Tick />
              </div>

              <div className="row header">&nbsp;</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">100K / month</div>

              <div className="row header">&nbsp;</div>
              <div className="row">5 users max</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>
              <div className="row">-</div>

              <div className="row header">&nbsp;</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">Priority Chat, Email</div>
            </div>
          </div>

          <div
            className={clsx("column plan enterprise", {
              current: plan_type === SubscriptionPlanType.Enterprise,
            })}
          >
            <div className={`planBox`}>
              <div className={clsx("planDetails", { centered: enableSelfServeAiDashboards })}>
                <div className="planTitle">Enterprise</div>
                {enableSelfServeAiDashboards ? (
                  <React.Fragment>
                    <PlanDescription>
                      {"For teams that need advanced controls, security, & dedicated support"}
                    </PlanDescription>
                    <div className="priceRow letsChat">
                      <ChatSmileIcon />
                      Let&rsquo;s chat
                    </div>
                  </React.Fragment>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      ...(shouldShowGmailDiscount ? { marginBottom: "38px" } : {}),
                    }}
                  >
                    <span className="planPrice">
                      <CommentOutlined style={{ fontSize: "32px" }} />
                    </span>{" "}
                    <span style={{ marginLeft: "12px", lineHeight: "1.25" }}>
                      volume discounts for teams
                    </span>
                  </div>
                )}
              </div>

              {showWebDashboardOffer && (
                <WebDashboardSlot $height={webDashboardSlotHeight}>
                  {webDashboardBoxed ? (
                    <WebDashboardBox>volume discounts for teams</WebDashboardBox>
                  ) : (
                    <WebDashboardAddonRow className="stacked">
                      <div className="addonMain">
                        <div className="addonTitle">
                          AI Dashboards
                          <NewBadge>NEW</NewBadge>
                        </div>
                      </div>
                      {webDashboardSlotExpanded && (
                        <div className="addonPrice">volume discounts for teams</div>
                      )}
                    </WebDashboardAddonRow>
                  )}
                </WebDashboardSlot>
              )}

              <Button className={`ctaBtn`} type="primary" onClick={onEnterprisePlanClick}>
                Contact Us
              </Button>

              {showBuildersRow && <div className="row builders">Custom</div>}
              <div className="row header">&nbsp;</div>
              <div className="row">Custom</div>
              <div className="row">Custom</div>
              <div className="row">Unlimited</div>
              <div className="row">Unlimited</div>
              <div className="row">Hourly</div>
              <div className="row">
                <Tick />
              </div>

              {enableSelfServeAiDashboards && (
                <>
                  <div className="row header">&nbsp;</div>
                  <div className="row">Custom</div>
                  <div className="row">
                    <Tick />
                  </div>
                  <div className="row">
                    <Tick />
                  </div>
                </>
              )}

              <div className="row header">&nbsp;</div>
              <div className="row">Unlimited</div>
              <div className="row">Unlimited</div>
              <div className="row">Hourly</div>

              <div className="row header">&nbsp;</div>
              <div className="row">Unlimited</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row header">&nbsp;</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">100K / month</div>

              <div className="row header">&nbsp;</div>
              <div className="row">Custom</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">
                <Tick />
              </div>

              <div className="row header">&nbsp;</div>
              <div className="row">
                <Tick />
              </div>
              <div className="row">Dedicated</div>
            </div>
          </div>

          <div className="column otherUsers">
            {plan_type && (
              <FlexColumn
                style={{
                  marginBottom: "24px",
                  ...(enableSelfServeAiDashboards ? { gap: 11 } : {}),
                }}
              >
                <div style={sideCardStyle}>
                  <FlexRow
                    style={{ justifyContent: "space-between", alignItems: "flex-start" }}
                    gap={0}
                  >
                    <FlexColumn gap={0}>
                      <Typography color="textPrimary" variant="body2" fontWeight={600}>
                        Billing Frequency
                      </Typography>
                      <Typography color="textSecondary" variant="body2">
                        {subscriptionDetails.billing_interval === "annually" ? "Annual" : "Monthly"}
                      </Typography>
                    </FlexColumn>

                    <Tooltip
                      placement="topLeft"
                      title={
                        subscriptionDetails.scheduled_change_type ===
                        SubscriptionScheduledChangeType.DowngradeToMonthlyBilling
                          ? `Your billing frequency will be changed to monthly starting on ${moment(
                              subscriptionDetails?.next_billing_cycle_dt
                            ).format("MMM DD, YYYY")}`
                          : undefined
                      }
                    >
                      <div>
                        <Button
                          disabled={
                            subscriptionDetails.scheduled_change_type ===
                            SubscriptionScheduledChangeType.DowngradeToMonthlyBilling
                          }
                          size="small"
                          noPadding
                          type="link"
                          onClick={updateBillingIntervalHandler}
                        >
                          Change
                        </Button>
                      </div>
                    </Tooltip>
                  </FlexRow>
                </div>

                <div style={sideCardStyle}>
                  <FlexColumn gap={0}>
                    <Typography color="textPrimary" variant="body2" fontWeight={600}>
                      Cancel Plan
                    </Typography>
                    <div style={{ color: COLORS.black65, fontSize: 14, lineHeight: 1.5 }}>
                      <CancelPlanHelpText
                        subscriptionDetails={subscriptionDetails}
                        clientFlags={clientFlags}
                        userEmail={currentUserEmail}
                        featureEnabled={true}
                      />
                    </div>
                  </FlexColumn>
                </div>
              </FlexColumn>
            )}
            {/* The Cancel Plan card above renders whenever plan_type is set. */}
            <FAQSection hideCancelFaq={Boolean(plan_type)} />
          </div>
        </FlexRow>
      </FlexColumn>
    </SelectorWrapV2>
  );

  return (
    <div>
      <ContactSalesModal
        showModal={showContactSalesModal}
        description={contactSalesConfig.description}
        hideTextarea={contactSalesConfig.hideTextarea}
        gateProps={contactSalesConfig.gateProps}
        onCancel={() => setShowContactSalesModal(false)}
      />
      {showAddWebDashboardModal && (
        <AddWebDashboardModal
          visible={showAddWebDashboardModal}
          onClose={() => setShowAddWebDashboardModal(false)}
        />
      )}
      {searchParams.get("from") === "billing" ? (
        <Button
          noPadding
          type="link"
          onClick={() => {
            history.goBack();
          }}
        >
          Back
        </Button>
      ) : null}

      {PerPlanDetails}

      <FreePlanBox $selfServe={enableSelfServeAiDashboards}>
        <h2 style={{ margin: 0 }}>Free Plan</h2>

        <div>
          Our free plan allows importing live data from any source into Google Sheets & Excel. Auto
          data refresh and other features are limited.{" "}
          <a href="https://coefficient.io/pricing" target="_blank">
            See free plan details
          </a>
        </div>

        {paidPlanTypes.includes(subscriptionDetails?.plan_type as SubscriptionPlanType) ? (
          <Button
            type="primary"
            onClick={() => {
              setContactSupportInfoBox({
                title: "Downgrade to Free",
                message: (
                  <>
                    To downgrade your plan to Free, please contact us at{" "}
                    <a href="mailto:support@coefficient.io" target="_blank">
                      support@coefficient.io
                    </a>
                    .
                    {enableSelfServeAiDashboards &&
                      subscriptionDetails?.has_web_dashboard_addon && (
                        <div style={{ marginTop: 8 }}>
                          Note: your AI Dashboards will be archived when the downgrade takes effect.
                        </div>
                      )}
                  </>
                ),
              });
            }}
          >
            Downgrade
          </Button>
        ) : null}
      </FreePlanBox>

      <ConfirmBox
        title={
          currentUserPlanManager
            ? `${planNameFromType(subscriptionDetails?.plan_type ?? "")} plan managed by ${
                currentUserPlanManager.first_name
              } ${currentUserPlanManager.last_name}`
            : "Please assign at least one user"
        }
        visible={showManagerPrompt}
        onOk={() => setShowManagerPrompt(false)}
        onCancel={() => setShowManagerPrompt(false)}
        okText="Got it"
        width={340}
      >
        {currentUserPlanManager
          ? `Please reach out to ${
              currentUserPlanManager.first_name ?? ""
            } to make any changes to the plan or to add/remove user licenses.`
          : "Please assign at least one user before modifying the plan and billing settings"}
      </ConfirmBox>

      <ConfirmBox
        title="🎉 Free plan!"
        visible={showTrialDowngradeInfo}
        okText="Got it"
        onOk={() => setShowTrialDowngradeInfo(false)}
        width={400}
      >
        You'll be downgraded to the Free Plan when your trial expires.
      </ConfirmBox>

      <ConfirmBox
        title="Remove users from plan before downgrading"
        visible={showInvalidStarterDowngrade}
        okText="Manage Licenses"
        cancelText="Cancel"
        onCancel={() => {
          setShowInvalidStarterDowngrade(false);
        }}
        okButton={
          <Button
            type="primary"
            onClick={() => {
              setShowInvalidStarterDowngrade(false);
              handleManageTeam();
            }}
          >
            Manage Licenses
          </Button>
        }
        onOk={() => {
          setShowInvalidStarterDowngrade(false);
          handleManageTeam();
        }}
        width={400}
      >
        You currently have multiple user licenses on your Pro plan. The Starter plan only supports
        one license. Remove other users from your plan before downgrading.
      </ConfirmBox>

      <ConfirmBox
        title="Downgrade to Starter?"
        confirmLoading={downgradeLoading}
        visible={showDowngradeConfirm !== null}
        okText="Downgrade"
        cancelText="Cancel"
        cancelButton={
          <Button type="link" noPadding>
            Cancel
          </Button>
        }
        closable={true}
        onCancel={() => setShowDowngradeConfirm(null)}
        onOk={() => {
          if (showDowngradeConfirm === "starter") {
            downgradeToStarterShowPricing();
            setShowDowngradeConfirm(null);
          }
        }}
        width={600}
      >
        <>
          <p>
            The Starter plan is for individual use only. You won&apos;t be able to add new users
            after downgrading.
          </p>

          <LoseAccessBox>
            <div className="loseHeader">
              <ExclamationCircleFilled /> You&apos;ll lose access to:
            </div>
            <ul>
              <li>Hourly auto refresh</li>
              <li>Scheduled bulk exports</li>
              <li>Dynamic recipient data alerts</li>
              {enableSelfServeAiDashboards && subscriptionDetails?.has_web_dashboard_addon && (
                <li>AI Dashboards (any current dashboards will be archived)</li>
              )}
            </ul>
          </LoseAccessBox>

          {DowngradeWarning}
        </>
      </ConfirmBox>

      <ConfirmBox
        confirmLoading={downgradeLoading}
        title="Confirm downgrade"
        visible={showStarterDowngradePricingSummary}
        okText="Confirm Change"
        cancelText="Cancel"
        cancelButton={
          <Button type="link" noPadding>
            Cancel
          </Button>
        }
        closable={true}
        onCancel={() => setShowStarterDowngradePricingSummary(false)}
        onOk={() => downgradeToStarter()}
        width={400}
      >
        <>
          <p>You're changing from Pro to Starter.</p>

          <p>
            Your current billing period ends{" "}
            {moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM DD, YYYY")}. You will
            then be downgraded to the Starter plan
          </p>

          <PricingSummaryWrap>
            <h3>Starter Plan</h3>
            {newPriceSummary && (
              <PriceSummaryProrated
                editType="downgrade-to-starter"
                newPriceSummary={newPriceSummary}
              />
            )}
          </PricingSummaryWrap>

          {DowngradeWarning}
        </>
      </ConfirmBox>

      <ConfirmBox
        title={`Change to ${newBillingInterval === "annually" ? "annually" : "monthly"} billing`}
        visible={newBillingInterval !== null}
        okText={`Change to ${newBillingInterval === "annually" ? "Annually" : "Monthly"}`}
        cancelText="Cancel"
        onOk={() => {
          if (!userConfirmChangeBillingCycle) {
            setUserConfirmChangeBillingCycleHighlight(true);
            return;
          }
          updateBillingInterval();
          track("billing_frequency_update_clicked");
        }}
        okButtonProps={{
          disabled: updateBillingIntervalLoading,
        }}
        onCancel={() => {
          setNewBillingInterval(null);
          setUserConfirmChangeBillingCycleHighlight(false);
          setUserConfirmChangeBillingCycle(false);
        }}
        width={440}
        confirmLoading={updateBillingIntervalLoading}
      >
        {newBillingInterval === "annually" ? (
          <>
            <PricingSummaryWrap>
              <h3>{planNameFromType(plan_type)} Plan</h3>
              {newBillingIntervalPricing && (
                <PriceSummaryProrated
                  editType={"edit-billing-interval"}
                  newPriceSummary={newBillingIntervalPricing}
                />
              )}
            </PricingSummaryWrap>
            <ConfirmCheckbox
              className={userConfirmChangeBillingCycleHighlight ? "error" : ""}
              checked={userConfirmChangeBillingCycle}
              onClick={e => {
                e.stopPropagation();
              }}
              onChange={e => {
                e.stopPropagation();
                const checked = e.target.checked;
                if (userConfirmChangeBillingCycleHighlight && checked) {
                  setUserConfirmChangeBillingCycleHighlight(false);
                }
                setUserConfirmChangeBillingCycle(e.target.checked);
              }}
            >
              <Typography
                variant="body1"
                fontWeight={600}
                color={userConfirmChangeBillingCycleHighlight ? "error" : "inherit"}
              >{`I want to change to annually billing`}</Typography>
            </ConfirmCheckbox>
          </>
        ) : (
          <>
            <p>
              Your current billing period ends{" "}
              {moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM DD, YYYY")}.
            </p>

            <p
              style={{
                borderRadius: "5px",
                color: COLORS.black65,
                background: COLORS.yellowBg2,
                padding: "8px 8px 8px 8px",
              }}
            >
              Starting {moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM DD, YYYY")},
              we will charge you ${newBillingIntervalPricing?.dueNowCost} per month on your card
              ending in {subscriptionDetails?.cc_last_4_digits}.
            </p>

            <ConfirmCheckbox
              className={userConfirmChangeBillingCycleHighlight ? "error" : ""}
              checked={userConfirmChangeBillingCycle}
              onClick={e => {
                e.stopPropagation();
              }}
              onChange={e => {
                e.stopPropagation();
                const checked = e.target.checked;
                if (userConfirmChangeBillingCycleHighlight && checked) {
                  setUserConfirmChangeBillingCycleHighlight(false);
                }
                setUserConfirmChangeBillingCycle(e.target.checked);
              }}
            >
              <Typography
                variant="body1"
                color={userConfirmChangeBillingCycleHighlight ? "error" : "inherit"}
                fontWeight={600}
              >{`I want to change to monthly billing`}</Typography>
            </ConfirmCheckbox>
          </>
        )}
      </ConfirmBox>
    </div>
  );
};
